'use client'

import { Flex, Grid, GridItem, VStack } from '@chakra-ui/react'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import { GrandAspectRatio, GrandHeading, GrandSection, HTMLArticle } from 'ui'
import type { MapWidgetType, StorefrontWidget } from 'ecosystem'
import { type WidgetImplementation } from '../../registry/typings'
import { DEMO_MAP_ID, MAPS_LIBRARIES } from '../MapLocatorWidget/constants'
import { MAP_WIDGET_CN, mapWidgetCn as cn } from './constants'
import PinMarker from './PinMarker'

export type MapWidgetProps = WidgetImplementation<StorefrontWidget<MapWidgetType>>

/*
 * Uses the same loader as MAP_LOCATOR. Two different Google Maps scripts on one page
 * overwrite each other's `window.google.maps`, after which objects built by one
 * instance fail the `instanceof` checks inside the other (fitBounds, clusterer setMap).
 * Keep every map in the app on @vis.gl/react-google-maps.
 */
export function MapWidget({ widget, widgetId }: MapWidgetProps) {
  const { title, description, lat, lng, sub, layout } = widget.widget
  const position = { lat: Number(lat), lng: Number(lng) }

  const renderMap = () => (
    <APIProvider
      apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ''}
      libraries={MAPS_LIBRARIES}
    >
      <Map
        defaultCenter={position}
        defaultZoom={18}
        disableDefaultUI
        mapId={DEMO_MAP_ID}
        mapTypeControl={false}
        mapTypeId="roadmap"
        rotateControl={false}
        scaleControl={false}
        zoomControl={false}
      >
        <AdvancedMarker position={position}>
          <PinMarker />
        </AdvancedMarker>
      </Map>
    </APIProvider>
  )

  const renderLayout = () => {
    switch (layout) {
      case 'COMPACT': {
        return (
          <Grid
            gap={{
              base: 5,
              md: 10
            }}
            templateColumns={{
              base: '1fr',
              md: 'repeat(10, 1fr)'
            }}
          >
            <GridItem
              colSpan={{
                base: 1,
                md: 10
              }}
              order={{
                base: 2,
                md: 1
              }}
              overflow="hidden"
              rounded="md"
            >
              <GrandAspectRatio breakpoints={{ base: '16:9' }}>{renderMap()}</GrandAspectRatio>
            </GridItem>

            {description ? (
              <GridItem
                as={Flex}
                className={cn(MAP_WIDGET_CN.DESC)}
                colSpan={{
                  base: 1,
                  md: sub ? 6 : 10
                }}
                order={{
                  base: 1,
                  md: 2
                }}
                overflow="hidden"
                rounded="md"
              >
                {description}
              </GridItem>
            ) : null}

            {sub ? (
              <GridItem
                as={Flex}
                className={cn(MAP_WIDGET_CN.CONTENT)}
                colSpan={{
                  base: 1,
                  md: description ? 4 : 10
                }}
                order={3}
              >
                <HTMLArticle content={sub} w="full" />
              </GridItem>
            ) : null}
          </Grid>
        )
      }
      default:
        return (
          <Grid
            gap={{
              base: 5,
              md: 10
            }}
            templateColumns={{
              base: '1fr',
              md: 'repeat(10, 1fr)'
            }}
            templateRows={{
              base: '1fr',
              md: 'repeat(2, 1fr)'
            }}
          >
            <GridItem
              colSpan={{
                base: 1,
                md: 6
              }}
              order={{
                base: 2,
                md: 1
              }}
              overflow="hidden"
              rounded="md"
              rowSpan={{
                base: 1,
                md: 2
              }}
            >
              <GrandAspectRatio breakpoints={{ base: '16:9', md: '4:3' }}>
                {renderMap()}
              </GrandAspectRatio>
            </GridItem>

            {description ? (
              <GridItem
                alignItems="center"
                as={Flex}
                className={cn(MAP_WIDGET_CN.DESC)}
                colSpan={{
                  base: 1,
                  md: 4
                }}
                order={{
                  base: 1,
                  md: 2
                }}
                rowSpan={sub ? 1 : { base: 1, md: 2 }}
              >
                {description}
              </GridItem>
            ) : null}

            {sub ? (
              <GridItem
                alignItems="center"
                as={Flex}
                className={cn(MAP_WIDGET_CN.CONTENT)}
                colSpan={{
                  base: 1,
                  md: 4
                }}
                order={3}
                rowSpan={description ? 1 : { base: 1, md: 2 }}
              >
                <HTMLArticle content={sub} w="full" />
              </GridItem>
            ) : null}
          </Grid>
        )
    }
  }

  return (
    <GrandSection
      className={cn(MAP_WIDGET_CN.SECTION)}
      contentContainerProps={{
        className: cn(MAP_WIDGET_CN.SECTION_CONTAINER)
      }}
      sectionId={widgetId}
    >
      <VStack alignItems="stretch" className={cn(MAP_WIDGET_CN.WRAPPER)} spacing={3}>
        {title ? <GrandHeading className={cn(MAP_WIDGET_CN.TITLE)} title={title} /> : null}

        {renderLayout()}
      </VStack>
    </GrandSection>
  )
}
